/**
 * Azioni ISO 9001 della segnalazione (QA26-F4.10): correttiva immediata e preventiva, assegnate a funzione e persona,
 * con scadenza, stato e aggiornamenti. Usate nella scheda e in «Le mie azioni».
 */
import { App, Button, Card, Col, DatePicker, Form, Input, Modal, Radio, Row, Select, Space, Table, Tag } from "antd";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import dayjs, { type Dayjs } from "dayjs";
import { STATI_AZIONE, TIPI_AZIONE, type AzioneDto, type OwnerDto, type SegnalazioneDto, type StatoAzione, type TipoAzione } from "@qa/shared";
import { ANCORA } from "@qa/shared";
import { api } from "../../../api/client";
import { fmtData } from "../../../lib/format";
import { useAggiorna } from "../../../lib/query";
import { messaggioErrore } from "../../../i18n/errori";
import { OwnerInput, SelectLista } from "../../../components/campi";
import { Miss, Owner, Storico, Vuoto } from "../../../components/ui";
import { palette } from "../../../theme";

const COLORE_STATO: Record<StatoAzione, string> = { aperta: "default", in_corso: "processing", fatta: "success", verificata: "green" };

export function StatoAzioneTag({ s, scaduta }: { s: StatoAzione; scaduta?: boolean }) {
  const { t } = useTranslation();
  return (
    <Space size={4}>
      <Tag color={COLORE_STATO[s]}>{t(`dominio.statoAzione.${s}`)}</Tag>
      {scaduta && <Tag color="red">{t("redflag.azioni.scaduta")}</Tag>}
    </Space>
  );
}

export function TipoAzioneTag({ tipo }: { tipo: TipoAzione }) {
  const { t } = useTranslation();
  return <Tag color={tipo === "correttiva" ? palette.warn : palette.primary}>{t(`dominio.tipoAzione.${tipo}`)}</Tag>;
}

type ValoriAzione = { tipo: TipoAzione; descrizione: string; funzione?: string | null; owner?: OwnerDto | null; scadenza?: Dayjs | null };

/** Crea o modifica (descrizione, funzione, persona, scadenza): chi modifica la segnalazione. */
export function AzioneModal({ segnalazioneId, azione, tipo, onClose }: { segnalazioneId: string; azione?: AzioneDto; tipo?: TipoAzione; onClose: () => void }) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const aggiorna = useAggiorna();
  const [form] = Form.useForm<ValoriAzione>();
  const m = useMutation({
    mutationFn: (v: ValoriAzione) => {
      const body = {
        tipo: v.tipo,
        descrizione: v.descrizione.trim(),
        funzione: v.funzione ?? null,
        owner: v.owner?.nome || v.owner?.utenteId ? v.owner : null,
        scadenza: v.scadenza ? v.scadenza.format("YYYY-MM-DD") : null,
      };
      return azione ? api.patch<AzioneDto>(`/redflag/azioni/${azione.id}`, body) : api.post<AzioneDto>(`/redflag/segnalazioni/${segnalazioneId}/azioni`, body);
    },
    onSuccess: (a) => {
      message.success(azione ? t("comune.salvato") : t("redflag.azioni.creataOk", { codice: a.codice }));
      void aggiorna();
      onClose();
    },
    onError: (e) => message.error(messaggioErrore(e)),
  });
  return (
    <Modal open onCancel={onClose} title={azione ? `${t("redflag.azioni.modifica")} · ${azione.codice}` : t("redflag.azioni.nuova")} okText={t("azioni.salva")} cancelText={t("azioni.annulla")} confirmLoading={m.isPending} onOk={() => form.submit()} destroyOnHidden width={600}>
      <Form<ValoriAzione>
        form={form}
        layout="vertical"
        initialValues={{
          tipo: azione?.tipo ?? tipo ?? "correttiva",
          descrizione: azione?.descrizione ?? "",
          funzione: azione?.funzione ?? null,
          owner: azione?.owner ?? null,
          scadenza: azione?.scadenza ? dayjs(azione.scadenza) : null,
        }}
        onFinish={(v) => m.mutate(v)}
      >
        <Form.Item name="tipo" label={t("redflag.azioni.tipo")}>
          <Radio.Group optionType="button" options={TIPI_AZIONE.map((x) => ({ value: x, label: t(`dominio.tipoAzione.${x}`) }))} />
        </Form.Item>
        <Form.Item name="descrizione" label={t("redflag.azioni.descrizione")} rules={[{ required: true, whitespace: true, message: t("redflag.nuova.obbligatorio") }]}>
          <Input.TextArea autoSize={{ minRows: 3, maxRows: 10 }} maxLength={4000} />
        </Form.Item>
        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item name="funzione" label={t("redflag.azioni.funzione")}>
              <SelectLista lista="funzione_nc" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="owner" label={t("redflag.azioni.owner")}>
              <OwnerInput placeholder={t("redflag.azioni.ownerPh")} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="scadenza" label={t("redflag.azioni.scadenza")}>
          <DatePicker format="DD/MM/YYYY" style={{ width: 200 }} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

/** Aggiorna stato e nota: l'owner e chi modifica la segnalazione. «Verificata» solo qualità e admin. */
export function AggiornaAzioneModal({ azione, onClose }: { azione: AzioneDto; onClose: () => void }) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const aggiorna = useAggiorna();
  const [stato, setStato] = useState<StatoAzione>(azione.stato);
  const [nota, setNota] = useState("");
  const m = useMutation({
    mutationFn: () => api.patch<AzioneDto>(`/redflag/azioni/${azione.id}`, { ...(stato !== azione.stato ? { stato } : {}), nota: nota.trim() || null }),
    onSuccess: () => {
      message.success(t("comune.salvato"));
      void aggiorna();
      onClose();
    },
    onError: (e) => message.error(messaggioErrore(e)),
  });
  const cambiato = stato !== azione.stato || !!nota.trim();
  return (
    <Modal open onCancel={onClose} title={t("redflag.azioni.aggiornaTitolo", { codice: azione.codice })} okText={t("azioni.salva")} cancelText={t("azioni.annulla")} okButtonProps={{ disabled: !cambiato }} confirmLoading={m.isPending} onOk={() => m.mutate()} destroyOnHidden width={600}>
      <p style={{ whiteSpace: "pre-wrap", marginTop: 0 }}>{azione.descrizione}</p>
      <div className="muted small">{t("redflag.azioni.stato")}</div>
      <Select
        value={stato}
        onChange={setStato}
        style={{ width: 220, margin: "4px 0 12px" }}
        options={STATI_AZIONE.map((x) => ({ value: x, label: t(`dominio.statoAzione.${x}`), disabled: x === "verificata" && !azione.verificabile && azione.stato !== "verificata" }))}
      />
      {!azione.verificabile && <div className="muted small">{t("redflag.azioni.verificaSoloQualita")}</div>}
      <div className="muted small" style={{ marginTop: 8 }}>
        {t("redflag.azioni.nota")}
      </div>
      <Input.TextArea rows={3} maxLength={4000} placeholder={t("redflag.azioni.notaPh")} value={nota} onChange={(e) => setNota(e.target.value)} />
      <Card size="small" title={t("redflag.azioni.storico")} style={{ marginTop: 14 }}>
        <Storico righe={azione.aggiornamenti} vuoto={t("redflag.scheda.nessunAggiornamento")} />
      </Card>
    </Modal>
  );
}

/** Tabella delle azioni: nella scheda (senza colonna segnalazione) e in «Le mie azioni». */
export function TabellaAzioni({ righe, conSegnalazione, onModifica, loading }: { righe: AzioneDto[]; conSegnalazione?: boolean; onModifica?: (a: AzioneDto) => void; loading?: boolean }) {
  const { t } = useTranslation();
  const [agg, setAgg] = useState<AzioneDto | null>(null);
  return (
    <>
      <Table<AzioneDto>
        size="small"
        rowKey="id"
        pagination={conSegnalazione ? { pageSize: 50, hideOnSinglePage: true } : false}
        loading={loading}
        dataSource={righe}
        scroll={{ x: 760 }}
        columns={[
          { title: "", key: "c", width: 90, render: (_, a) => <span className="mono" style={{ whiteSpace: "nowrap" }}>{a.codice}</span> },
          ...(conSegnalazione
            ? [
                {
                  title: t("redflag.azioni.segnalazione"),
                  key: "rf",
                  render: (_: unknown, a: AzioneDto) => (
                    <Link to={`/redflag/segnalazioni/${a.segnalazione.id}`}>
                      <span className="mono">{a.segnalazione.codice}</span> · {a.segnalazione.cliente}
                    </Link>
                  ),
                },
              ]
            : []),
          { title: t("redflag.azioni.tipo"), key: "t", width: 150, render: (_, a) => <TipoAzioneTag tipo={a.tipo} /> },
          {
            title: t("redflag.azioni.descrizione"),
            key: "d",
            render: (_, a) => (
              <div style={{ whiteSpace: "pre-wrap", minWidth: 220, maxWidth: 420 }}>
                {a.descrizione}
                {a.funzione && <div className="muted small">{a.funzione}</div>}
              </div>
            ),
          },
          { title: t("redflag.azioni.owner"), key: "o", width: 150, render: (_, a) => (a.owner.nome ? <Owner o={a.owner} /> : <Miss>{t("comune.daAssegnare")}</Miss>) },
          { title: t("redflag.azioni.scadenza"), key: "s", width: 100, render: (_, a) => <span className={a.scaduta ? "miss num" : "num"}>{fmtData(a.scadenza)}</span> },
          { title: t("redflag.azioni.stato"), key: "st", width: 150, render: (_, a) => <StatoAzioneTag s={a.stato} scaduta={a.scaduta} /> },
          {
            title: "",
            key: "az",
            width: 170,
            render: (_, a) => (
              <Space size={4}>
                {a.aggiornabile && (
                  <Button size="small" onClick={() => setAgg(a)}>
                    {t("redflag.azioni.aggiorna")}
                  </Button>
                )}
                {onModifica && (
                  <Button size="small" type="text" onClick={() => onModifica(a)}>
                    {t("azioni.modifica")}
                  </Button>
                )}
              </Space>
            ),
          },
        ]}
      />
      {agg && <AggiornaAzioneModal azione={agg} onClose={() => setAgg(null)} />}
    </>
  );
}

export function AzioniCard({ r }: { r: SegnalazioneDto }) {
  const { t } = useTranslation();
  const [nuova, setNuova] = useState<TipoAzione | null>(null);
  const [modifica, setModifica] = useState<AzioneDto | null>(null);
  return (
    <Card
      size="small"
      id="azioni"
      title={t("redflag.azioni.titolo")}
      style={{ marginTop: 16 }}
      data-tip={ANCORA.azioni}
      extra={
        r.puo.creareAzioni && (
          <Space size={6} wrap>
            <Button size="small" type="primary" onClick={() => setNuova("correttiva")}>
              {t("redflag.azioni.crea")}
            </Button>
          </Space>
        )
      }
    >
      <p className="muted small" style={{ margin: "0 0 10px" }}>
        {t("redflag.azioni.spiega")}
      </p>
      {r.azioni.length ? (
        <TabellaAzioni righe={r.azioni} onModifica={r.puo.creareAzioni ? setModifica : undefined} />
      ) : (
        <Vuoto titolo={t("redflag.azioni.vuotaTitolo")} testo={r.puo.creareAzioni ? t("redflag.azioni.vuota") : undefined} />
      )}
      {nuova && <AzioneModal segnalazioneId={r.id} tipo={nuova} onClose={() => setNuova(null)} />}
      {modifica && <AzioneModal segnalazioneId={r.id} azione={modifica} onClose={() => setModifica(null)} />}
    </Card>
  );
}
